import axios from '@axios'
import { storePagination, purchaseRequisitionDomains, sorting, alerts } from '@/mixins'

export const getInitialPurchaseRequisitionItem = () => ({
  id: null,
  quantity: 0,
  quantityInStock: 0,
  unitsPerPack: null,
  sku: null,
})

export const getInitialPurchaseRequisition = () => ({
  id: null,
  loaded: false,
  store: {},
  storeId: null,
  group: null,
  groupId: null,
  items: [],
  purchaseOrders: [],
  payments: [],
  productsValue: 0,
  status: 'Draft',
  origin: 'Manual',
  observation: null,
})

const getInitialState = () => ({
  ...storePagination.state(),
  busy: false,
  store: null,
  purchaseRequisition: getInitialPurchaseRequisition(),
  lastApprovalFlow: null,
  skuSearchResults: [],
  files: [],
  filters: {
    skuId: '',
    ean: null,
    name: null,
    maximumStock: null,
    belowMinimumStock: true,
    departmentId: null,
    brandId: null,
    groupId: null,
    subgroupId: null,
  },
})

export default {
  namespaced: true,

  state: getInitialState(),

  getters: {
    isEdit(state) {
      return state.purchaseRequisition.status !== 'Completed'
    },
    managementAccountsOptions(state) {
      return state.managementAccounts?.map(item => ({
        ...item,
        value: item.id,
        label: item.name,
      }))
    },
    readOnly(state) {
      return purchaseRequisitionDomains.methods.mxIsPurchaseRequisitionReadOnly(
        state.purchaseRequisition
      )
    },
    approvalMessage(state) {
      if (!state.lastApprovalFlow) {
        return null
      }

      const approvalsArray = []
      state.lastApprovalFlow.steps.forEach(step => {
        // Para cada step, percorra cada approvalGroup
        step.approvalGroups.forEach(approvalGroup => {
          // Para cada approvalGroup, adicione todos os objetos dentro de "approvals" ao array final
          approvalsArray.push(...approvalGroup.approvals)
        })
      })
      const approvalWithMessage = approvalsArray.find(approval =>
        ['ChangesRequested', 'Disapproved'].includes(approval.status)
      )
      return approvalWithMessage?.observation
    },
  },

  mutations: {
    ...storePagination.mutations,

    RESET_STATE(state) {
      const initial = getInitialState()
      Object.keys(initial).forEach(key => {
        state[key] = initial[key]
      })
    },

    SET_PURCHASE_REQUISITION(state, val) {
      state.purchaseRequisition = val
      state.purchaseRequisition.originalSupplier = val.supplier
      state.purchaseRequisition.loaded = true
    },
    SET_LAST_APPROVAL_FLOW(state, val) {
      state.lastApprovalFlow = val
    },
    SET_ITEMS_PURCHASE(state, itemsPurchase) {
      state.purchaseRequisition.items = itemsPurchase
    },
    SET_STORE(state, store) {
      state.store = store
    },
    SET_SKU_SEARCH_RESULT(state, skuSearchResults) {
      state.skuSearchResults = skuSearchResults
    },
    SET_BUSY(state, busy) {
      state.busy = busy
    },

    UPDATE_ITEM(state, { index, item }) {
      state.itemsPurchase.items.splice(index, 1, {
        ...state.itemsPurchase.items[index],
        ...item,
      })
    },

    REMOVE_ITEM(state, index) {
      state.purchaseRequisition.items.splice(index, 1)
    },

    CLEAN_STATE_TABS(state) {
      const { itemsPurchase, combos, lastApprovalFlow } = getInitialState()
      state.itemsPurchase = itemsPurchase
      state.combos = combos
      state.lastApprovalFlow = lastApprovalFlow
    },
    ADD_REQUISITION_ITEM(state, skuStock) {
      state.purchaseRequisition.items.push({
        ...getInitialPurchaseRequisitionItem(),
        skuId: skuStock.sku.id,
        sku: skuStock.sku,
        quantityInStock: skuStock.stock,
        unitsPerPack: skuStock.sku?.product?.unitsPerPack ?? null,
      })
    },
    CLEAN_FILTERS(state) {
      const { filters } = getInitialState()
      state.filters = filters
      state.skuSearchResults = []
    },
    CLEAN_ITEMS(state) {
      state.purchaseRequisition.items = []
    },
    SET_FILTER_VALUE(state, { key, value }) {
      state.filters[key] = value
    },
    CLEAN_SKU_SEARCH_RESULTS(state) {
      state.skuSearchResults = []
    },
    SET_FILES(state, files) {
      state.files = files
    },
    ADD_FILE(state, file) {
      state.files.push(file)
    },
    DELETE_FILE(state, file) {
      const index = state.files.findIndex(f => f.id === file.id)
      if (index > -1) {
        state.files.splice(index, 1)
      }
    },
    CLEAN_FILE_STATE(state) {
      state.files = []
    },
  },

  actions: {
    ...storePagination.actions,
    async fetchPurchaseRequisitionById(states, id) {
      const { data } = await axios.get(`/api/purchase-requisitions/${id}`)
      data.store = data.store ?? {}
      data.storeId = data.storeId.toString()
      return data
    },

    async fetchPurchaseRequisition({ commit, dispatch }, id) {
      commit('RESET_STATE')
      const data = await dispatch('fetchPurchaseRequisitionById', id)
      commit('SET_PURCHASE_REQUISITION', data)
      commit('SET_ITEMS_PURCHASE', data.items)
    },

    async fetchPurchaseRequisitionLastApprovalFlow({ commit }, id) {
      try {
        const { data } = await axios.get(`/api/purchase-requisitions/${id}/last-approval-flow`)
        commit('SET_LAST_APPROVAL_FLOW', data)
      } catch (err) {
        console.log(err)
      }
    },

    async generateItemsReport(context, purchaseRequisitionId) {
      const { data } = await axios.get(
        `/api/purchase-requisitions/${purchaseRequisitionId}/items/download`,
        { responseType: 'blob' }
      )
      return data
    },

    async savePurchaseRequisition({ state, commit }, { status }) {
      const body = state.purchaseRequisition
      if (status) {
        body.status = status
      }
      const { data } = await axios({
        url: `/api/purchase-requisitions${state.purchaseRequisition?.id ? `/${state.purchaseRequisition.id}` : ''}`,
        method: state.purchaseRequisition?.id ? 'PUT' : 'POST',
        data: body,
      })

      // Atualiza o estado com os dados retornados (incluindo o novo ID se for criação)
      commit('SET_PURCHASE_REQUISITION', data)
      commit('SET_ITEMS_PURCHASE', data.items)
      return data
    },

    async reevaluatePurchaseRequisition(
      { state },
      { itemsUnitValueRevision = false, tokenDelegated } = {}
    ) {
      const axiosOptions = {}
      if (itemsUnitValueRevision) {
        axiosOptions.headers = {
          'delegate-token': tokenDelegated,
        }
      }

      const body = {
        ...state.purchaseRequisition,
        supplier: state.purchaseRequisition.originalSupplier,
      }

      await axios({
        url: '/api/purchase-requisitions/reevaluate',
        method: 'PUT',
        data: { ...body, itemsUnitValueRevision },
        ...axiosOptions,
      })
    },

    async deletePurchaseRequisition(context, { purchaseRequisitionId, reason } = {}) {
      await axios.delete(`/api/purchase-requisitions/${purchaseRequisitionId}`, {
        data: {
          reason,
        },
      })
    },

    async cancelPurchaseRequisition(context, { purchaseRequisitionId, reason } = {}) {
      await axios.put(`/api/purchase-requisitions/${purchaseRequisitionId}/cancel`, {
        reason,
      })
    },

    async completePurchaseRequisition(context, purchaseRequisitionId) {
      const { data } = await axios.put(
        `/api/purchase-requisitions/${purchaseRequisitionId}/complete`
      )
      return data
    },

    async addSupplierOrder(context, { purchaseRequisitionId, requestedDate, orderNumber, supplierId, attachments } = {}) {
      const { data } = await axios.put(`/api/purchase-requisitions/${purchaseRequisitionId}/add-supplier-order`, {
        requestedDate,
        orderNumber,
        supplierId,
        attachments,
      })
      return data
    },

    async getSkus({ commit, state }) {
      commit('SET_BUSY', true)
      try {
        const { data } = await axios.get(`/api/stock-positions`, {
          params: {
            storeId: state.purchaseRequisition.storeId,
            ...state.filters,
            // Quando a requisição possui grupo, somente produtos deste grupo podem ser listados
            groupId: state.purchaseRequisition.groupId || state.filters.groupId,
            pageSize: state.paging.pageSize,
            pageIndex: state.paging.currentPage - 1,
            sortBy: sorting.methods.getSorting(state),
          },
        })

        commit('SET_BUSY', false)
        commit('SET_SKU_SEARCH_RESULT', data.results)
        commit('SET_PAGING', {
          ...state.paging,
          rowCount: data.rowCount,
          rowsInCurrentPage: data.rowsInCurrentPage,
        })
      } catch (error) {
        alerts.methods.showError({ error })
      } finally {
        commit('SET_BUSY', false)
      }
    },

    addRequisitonItem({ commit }, sku) {
      commit('ADD_REQUISITION_ITEM', sku)
    },

    cleanSkuSearchResults({ commit }) {
      commit('CLEAN_SKU_SEARCH_RESULTS')
    },

    setStore({ commit }, store) {
      commit('SET_STORE', store)
    },

    updateItem({ commit }, { index, item }) {
      if (typeof item !== 'object') throw new Error('Item is not a object')
      else if (typeof index !== 'number' || index < 0) throw new Error('index is not valid')

      commit('UPDATE_ITEM', { index, item })
    },

    removeItem({ commit }, { index, item }) {
      commit('REMOVE_ITEM', { index, item })
    },

    cleanState({ commit }) {
      commit('RESET_STATE')
    },

    cleanItems({ commit }) {
      commit('CLEAN_ITEMS')
    },
    resetFilters({ commit, state }) {
      commit('CLEAN_FILTERS')
      // Mantém o grupo da requisição aplicado ao filtro após a limpeza
      if (state.purchaseRequisition.groupId) {
        commit('SET_FILTER_VALUE', { key: 'groupId', value: state.purchaseRequisition.groupId })
      }
    },
    setFilterValue({ commit }, { key, value }) {
      commit('SET_FILTER_VALUE', { key, value })
    },
    setFiles({ commit }, files) {
      commit('SET_FILES', files)
    },
    deleteLocalFile({ commit }, file) {
      commit('DELETE_FILE', file)
    },
    cleanFileState({ commit }) {
      commit('CLEAN_FILE_STATE')
    },
    async uploadDocuments({ state }, entityKey) {
      // Filtrar apenas arquivos novos (sem id) que realmente têm um arquivo selecionado: o grupo
      // repetível sempre renderiza uma linha em branco e a exclusão deixa o item sem arquivo.
      const filesToUpload = state.files.filter(f => !f.id && f.file?.file)
      if (filesToUpload.length > 0) {
        const formData = new FormData()
        formData.append('entityId', entityKey)
        formData.append('uploadType', 'PurchaseRequisition')
        filesToUpload.forEach(fileItem => {
          formData.append('files', fileItem.file.file)
          formData.append('types', fileItem.type ?? 'Others')
        })
        const { data } = await axios.post(`/api/documents/upload-files`, formData)
        return data
      }
      return []
    },
  },
}
